/**
 * Utility functions for optimizing Supabase Storage images
 * Uses Supabase Image Transformation API to automatically resize and convert images
 */

/**
 * Optimize Supabase Storage image URL with transformation parameters
 * @param {string} imageUrl - Original Supabase Storage URL
 * @param {Object} options - Transformation options
 * @param {number} options.width - Desired width in pixels
 * @param {number} options.height - Desired height in pixels
 * @param {string} options.resize - Resize mode: 'cover', 'contain', 'fill'
 * @param {string} options.format - Output format: 'webp', 'jpg', 'png'
 * @param {number} options.quality - Image quality (1-100, default: 80)
 * @returns {string} Optimized image URL
 */
export const optimizeSupabaseImage = (imageUrl, options = {}) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return imageUrl
  }

  // Only transform Supabase Storage URLs
  if (!imageUrl.includes('supabase.co/storage')) {
    return imageUrl
  }

  // Default options for professional images
  const {
    width = 400,
    height = 400,
    resize = 'cover',
    format = 'webp',
    quality = 80
  } = options

  // Check if URL already has query parameters
  const hasParams = imageUrl.includes('?')
  const separator = hasParams ? '&' : '?'

  // Build transformation parameters
  const params = new URLSearchParams()
  params.set('width', width.toString())
  params.set('height', height.toString())
  params.set('resize', resize)
  params.set('format', format)
  params.set('quality', quality.toString())

  return `${imageUrl}${separator}${params.toString()}`
}

/**
 * Generate responsive srcset for Supabase images
 * @param {string} imageUrl - Original Supabase Storage URL
 * @param {Object} sizes - Size options
 * @returns {string} srcset string
 */
export const generateSupabaseSrcSet = (imageUrl, sizes = [200, 400, 800]) => {
  if (!imageUrl || !imageUrl.includes('supabase.co/storage')) {
    return null
  }

  return sizes.map(size => {
    const optimizedUrl = optimizeSupabaseImage(imageUrl, {
      width: size,
      height: size,
      resize: 'cover',
      format: 'webp',
      quality: 80
    })
    return `${optimizedUrl} ${size}w`
  }).join(', ')
}

/**
 * Optimize professional image for display
 * Automatically optimizes based on display size
 * @param {string} imageUrl - Professional image URL from database
 * @param {string} size - Size preset: 'thumbnail', 'small', 'medium', 'large'
 * @returns {string} Optimized image URL
 */
export const optimizeProfessionalImage = (imageUrl, size = 'medium') => {
  if (!imageUrl) return null

  const sizePresets = {
    thumbnail: { width: 100, height: 100 }, // For small avatars
    small: { width: 200, height: 200 },      // For list items
    medium: { width: 400, height: 400 },    // For cards (default)
    large: { width: 800, height: 800 }      // For modals/details
  }

  const preset = sizePresets[size] || sizePresets.medium

  return optimizeSupabaseImage(imageUrl, {
    ...preset,
    resize: 'cover',
    format: 'webp',
    quality: 85
  })
}
